import type { AnalyticsDashboardGroup, AnalyticsRoleContext, AnalyticsRoleInput } from './types';

// Subset of TEAM_ROLES from packages/ui/src/lib/constants.ts — kept local to avoid
// a circular dependency (@workspace/ui already imports @workspace/analytics).
const ANALYTICS_TEAM_ROLES = {
  TEAM_LEAD: 'teams_team_lead',
} as const;

// Product-confirmed role → dashboard bucket mapping. Roles outside this table
// (e.g. admin, finance, legal) intentionally resolve to 'unmapped'.
const ROLE_TO_DASHBOARD_GROUP: Record<string, AnalyticsDashboardGroup> = {
  employee: 'employee',
  employer: 'employer',
  individual_contractor: 'contractor',
  company_contractor: 'contractor',
  contractor_of_record: 'contractor',
  gig_worker: 'employee',
  organisation_owner: 'employer',
  payroll_seat: 'employee',
  draft_seat: 'employee',
  teams_global_admin: 'employee',
  b2c_employee: 'employee',
  b2c_standard: 'employee',
  b2c_free: 'employee',
};

function flattenTeamRoles(teams: AnalyticsRoleInput['teams']): string[] {
  if (!teams?.length) return [];

  const roles = new Set<string>();
  for (const team of teams) {
    for (const role of team.team_role ?? []) {
      roles.add(role);
    }
  }

  return [...roles];
}

export function resolveAnalyticsRoleContext(input: AnalyticsRoleInput): AnalyticsRoleContext {
  const roles = input.roles ?? [];
  const teams = input.teams ?? [];
  const teamRoles = flattenTeamRoles(teams);
  const isTeamLead = teamRoles.includes(ANALYTICS_TEAM_ROLES.TEAM_LEAD);
  const primaryRole = roles[0]?.trim() || undefined;

  return {
    roles,
    team_roles: teamRoles,
    is_team_lead: isTeamLead,
    organisation_id: input.organisationId ?? undefined,
    team_count: teams.length,
    people_role: primaryRole,
    dashboard_group: (primaryRole && ROLE_TO_DASHBOARD_GROUP[primaryRole]) || 'unmapped',
  };
}
